import React, { useCallback } from "react";
import {
  Chart,
  ChartTitle,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartXAxis,
  ChartXAxisItem,
  ChartYAxis,
  ChartYAxisItem,
  ChartArea,
  ChartTooltip,
  SeriesNotes,
} from "@progress/kendo-react-charts";
import "hammerjs";

interface WellSurveyPlot2Props {

  selectedRow: any;
  surveyData: any[];
  maxDls?: number;
  maxMd?: number;
  customerName?: string;
  wellName?: string;
  surveyMetaData?: {
    kop: number;
    topOfEspPump: number;
    bottomOfESPSensor: number;
    tailPipeDesander: number;
    tailPipeBottom: number;
  };
  schematicData?: {
    eoTubing: number;
    sensorDepth: number;
  } | null;
}
const notesConfig: SeriesNotes = {
  label: {
    position: "outside",
    background: "gray",
    color: "white",
    border: {
      color: "gray",
      width: 5,
      dashType: "solid",
    },
  },
  line: {
    color: "gray",
    length: 75,
  },
  icon: {
    type: "circle",
  },
  position: "right",
};
const WellSurveyPlot2: React.FC<WellSurveyPlot2Props> = ({
  selectedRow,
  surveyData,
  maxDls,
  maxMd,
  customerName = "--",
  wellName = "--",
  surveyMetaData,
  schematicData,
}) => {
  const minMd = surveyData?.[0]?.md;
  // Calculate KOP related data
  const getKOPData = useCallback(() => {
    if (surveyMetaData?.kop && surveyData) {
      const kopIndex = surveyData.findIndex((x) => x.md === surveyMetaData.kop);
      if (kopIndex > 0) {
        return [surveyData[kopIndex], surveyData[kopIndex - 1]];
      }
    }
    return null;
  }, [surveyData, surveyMetaData]);

  const kopData = getKOPData();
  const getESPData = useCallback(() => {
    if (schematicData && typeof schematicData.eoTubing === 'number' && typeof schematicData.sensorDepth === 'number' && schematicData.eoTubing > 0 && schematicData.sensorDepth > 0) {
      return [
        { dls: 0, md: schematicData.eoTubing },
        { dls: 0, md: schematicData.sensorDepth }
      ];
    }
    return [];
  }, [schematicData]);
 // Added new helper functions for data generation
 const getTubingData = useCallback(() => {
  if (schematicData && typeof schematicData.eoTubing === 'number' && schematicData.eoTubing > 0) {
    return [
      { dls: 2, md: 0 },             
      { dls: 2, md: schematicData.eoTubing }
    ];
  }
  return [];
}, [schematicData]);
  return (
    <Chart
      zoomable={{
        mousewheel: {
          lock: "x",
          rate: 0.03,
        },
        selection: {
          lock: "x",
        },
      }}
      className='height-max flex justify-center items-start p-4 gap-4 w-full'
    >
      <ChartArea
        background='transparent'
        height={900}
        width={900}
        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
      />

      <ChartTitle
        text={`Customer\t\t:\t${customerName}\nWellName\t\t:\t${wellName}`}
        color='#fff'
        font='18px Mulish'
        align='left'
      />

      <ChartLegend
        position='bottom'
        labels={{
          font: "16px Mulish",
          color: "#fff",
        }}
      />

      <ChartSeries>
        {/* INC Series */}
        <ChartSeriesItem
          type='scatterLine'
          data={surveyData}
          xField='inc'
          yField='md'
          name='INC'
          color='#f97316'
          style='smooth'
          markers={{
            type: "cross",
            size: 6,
            background: "#f97316",
            visible: false,
          }}
          tooltip={{
            border: {
              width: 0,
              color: "transparent",
            },
            visible: true,
            format: "INC: {0}, MD: {1}",
          }}
          width={2}
        />

        {/* DLS Series */}

        <ChartSeriesItem
          type='scatterLine'
          data={surveyData}
          xField='dls'
          yField='md'
          xAxis="DLS"
          name='DLS'
          color='#204887'
          style='smooth'
          markers={{
            type: "circle",
            size: 6,
            visible: false,
          }}
          tooltip={{
            visible: true,
            format: "DLS: {0}, MD: {1}", 
          }}
          width={2}
        />

{/* Tubing Series */}
{schematicData && typeof schematicData.eoTubing === 'number' && schematicData.eoTubing > 0 && (
  <ChartSeriesItem
    type="scatterLine"
    data={getTubingData()}
    xField="dls"
    yField="md"
    name="Tubing" 
    xAxis="DLS"
    color="#36454F"
    width={5}
    markers={{
      visible: false
    }}
    tooltip={{
      visible: true,
      format: "Tubing: {0}, MD: {1}"
    }}
    style="smooth"
  />
)}

        {/* ESP Series */}
        {schematicData &&
          schematicData?.eoTubing > 0 &&
          schematicData?.sensorDepth > 0 && (
            <ChartSeriesItem
              type='scatterLine'
              data={[
                { dls: 2, md: schematicData?.eoTubing },
                { dls: 2, md: schematicData?.sensorDepth },
              ]}
              xField='dls'
              yField='md'
              name='ESP'
              color='#DC4405'
              width={8}
              xAxis='DLS'
              markers={{
                visible: false,
              }}
              tooltip={{
                border: {
                  width: 0,
                },
                visible: true,
                format: "ESP: {0}, MD: {1}",
              }}
              dashType='solid'
              noteTextField='note'
              notes={{
                ...notesConfig,
                position: "right",
                icon: { type: "square" },
              }}
            />
          )}
        {/* TailPipe */}

        {schematicData &&
          schematicData?.sensorDepth > 0 &&
          surveyMetaData &&
          surveyMetaData?.tailPipeBottom > 0 && (
            <ChartSeriesItem
              type='scatterLine'
              data={[
                { dls: 2, md: schematicData?.sensorDepth },
                { dls: 2, md: surveyMetaData?.tailPipeBottom },
              ]}
              xField='dls'
              yField='md'
              name='TailPipe'
              color='#059bb4'
              width={2}
              xAxis='DLS'
              markers={{
                visible: false,
              }}
              tooltip={{
                border: {
                  width: 0,
                  color: "transparent",
                },
                visible: true,
                format: "TailPipe: {0}, MD: {1}",
              }}
              dashType='dash'
              noteTextField='note'
              notes={notesConfig}
            />
          )}
      </ChartSeries>

      <ChartYAxis>
        <ChartYAxisItem
          title={{
            text: "MD",
            font: "16px Mulish",
            color: "#fff",
          }}
          color='#fff'
          min={minMd}
          max={(maxMd ?? 0) + 1000}
          axisCrossingValue={[0, maxMd]}
          reverse={true}
          majorGridLines={{
            visible: true,
            width: 0.75,
            color: "#A0A0A0",
          }}
        />
      </ChartYAxis>
      <ChartXAxis>
                                                               
      <ChartXAxisItem
    name="INC"
    title={{
      text: "INC",
      font: "16px Verdana, sans-serif",
      color: "#fff",
      margin: 20
    }}
    axisCrossingValue={[0]}
    color="#fff"
    majorGridLines={{
      visible: true,
      width: 0.75,
      color: "#A0A0A0"
    }}
  />

        <ChartXAxisItem
          name='DLS'
          title={{
            text: "DLS",
            font: "16px Mulish",
            color: "#fff",
            margin: 20,
          }}
          color='#fff'
          min={0}
          max={(maxDls ?? 0) + 2}
          axisCrossingValue={[0]}
          reverse={false}
          majorGridLines={{
            visible: true,
            width: 0.75,
            color: "#A0A0A0",
          }}
          minorGridLines={{
            visible: false,
            width: 0.75,
            color: "#A0A0A0",
          }}
        />
      </ChartXAxis>
      <ChartTooltip
        shared={true}
        background='#1E293B'
        color='#fff'
        font='14px Mulish'
      />
    </Chart>
  );
};

export default WellSurveyPlot2;